import {DateTimeFormatter, LocalDate} from '@js-joda/core';
import Paper from '@material-ui/core/Paper';
import {lighten} from '@material-ui/core/styles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import ProgressBackdrop, {Progress} from "@tracker/shared/src/LoadingIndicator/ProgressBackdrop";
import RemoveProjectButton from '@tracker/shared/src/Scheduling/TableSchedule/RemoveProjectButton';
import ScheduleCell from '@tracker/shared/src/Scheduling/TableSchedule/ScheduleCell';
import TotalsRow from '@tracker/shared/src/Scheduling/TableSchedule/TotalsRow';
import {
    Employee,
    EmployeeSchedule,
    getEightWeeks,
    Project,
    ProjectSchedule
} from '@tracker/shared/src/Scheduling/Utils/EmployeeSchedulesCompiler';
import {useScheduleContext} from "@tracker/shared/src/Scheduling/Utils/ScheduleContextProvider";
import React, {useCallback, useMemo, useState} from 'react';
import AddEmployeeButton from './AddEmployeeButton';

const useStyles = makeStyles(theme => ({
    table: {
        '& .MuiTableCell-sizeSmall': {
            padding: '6px 12px 6px 8px',
        },
    },
    corner: {
        display: 'flex',
        alignItems: 'center',
    },
    header: {
        backgroundColor: lighten(theme.palette.primary.main, 0.6),
        '& th': {
            color: theme.palette.text.primary,
            fontWeight: 500,
        },
    },
    headerCell: {
        width: 100,
    },
    headerDate: {
        textAlign: 'center',
    },
    headerSub: {
        textAlign: 'center',
        fontWeight: 'normal',
    },
    rowHeader: {
        display: 'flex',
        alignItems: 'center',
    },
    rowTitle: {
        flexGrow: 1,
    },
}));

type Props = {
    project : Project;
    startDate : LocalDate;
    employeeSchedules : EmployeeSchedule[];
    currentWeek : string;
};

const dateFormatter = DateTimeFormatter.ofPattern('M/d');

export type IndividualSchedule = ProjectSchedule & {
    employee : Employee;
};

const ProjectScheduleTable : React.FC<Props> = ({project, startDate, employeeSchedules, currentWeek} : Props) => {
    const classes = useStyles();
    const scheduleContext = useScheduleContext();
    const [progress, setProgress] = useState<Progress | null>(null);
    const weeks = useMemo(() => getEightWeeks(startDate), [startDate]);

    const schedules = useMemo(() => employeeSchedules.reduce((schedules, employeeSchedule) => {
        const projectSchedule = employeeSchedule.schedules.find(schedule => schedule.project.id === project.id);

        if (!projectSchedule) {
            return schedules;
        }

        schedules.push({employee: employeeSchedule.employee, ...projectSchedule});
        return schedules;
    }, [] as IndividualSchedule[]), [project, employeeSchedules]);

    const handleFillRight = useCallback(async (employeeId : number, hours : number, startOfWeek : string) => {
        const weeks = schedules[0].weeks
            .filter(week => week.startOfWeek > startOfWeek)
            .map(week => ({
                startOfWeek: week.startOfWeek,
                scheduled: hours,
            }));

        await scheduleContext.fillRight(employeeId, project.id, weeks, setProgress);
        setProgress(null);
    }, [project.id, scheduleContext, schedules]);

    const handleApplyToAllVisible = useCallback(async (employeeId : number, hours : number, startOfWeek : string) => {
        const schedules = employeeSchedules.filter(es => es.schedules.find(schedule => schedule.project.id === project.id));

        await scheduleContext.applyToEmployees(
            employeeId,
            project,
            startOfWeek,
            startDate,
            hours,
            schedules,
            setProgress
        );
        setProgress(null);
    }, [employeeSchedules, project, scheduleContext, startDate]);

    return (
        <TableContainer component={Paper}>
            <ProgressBackdrop progress={progress}/>
            <Table size="small" className={classes.table}>
                <TableHead className={classes.header}>
                    <TableRow>
                        <TableCell>
                            <div className={classes.corner}>
                                <div className={classes.rowTitle}>{project.name}</div>
                                <AddEmployeeButton project={project} schedules={schedules} startDate={startDate}/>
                            </div>
                        </TableCell>
                        {weeks.map(week => (
                            <TableCell key={week.key} className={classes.headerCell}>
                                <div className={classes.headerDate}>{week.startDate.format(dateFormatter)}</div>
                                <div className={classes.headerSub}>Sch</div>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {schedules.map(schedule => (
                        <TableRow key={schedule.employee.id}>
                            <TableCell>
                                <div className={classes.rowHeader}>
                                    <div className={classes.rowTitle}>
                                        {schedule.employee.firstName} {schedule.employee.lastName}
                                    </div>
                                    <RemoveProjectButton
                                        employeeId={schedule.employee.id}
                                        projectId={schedule.project.id}
                                    />
                                </div>
                            </TableCell>

                            {schedule.weeks.map(week => (
                                <ScheduleCell
                                    key={week.startOfWeek}
                                    employeeId={schedule.employee.id}
                                    projectId={schedule.project.id}
                                    startOfWeek={week.startOfWeek}
                                    currentWeek={currentWeek}
                                    schedule={week}
                                    onFillRight={async (hours : number, startOfWeek : string) => {
                                        await handleFillRight(schedule.employee.id, hours, startOfWeek)
                                    }}
                                    onApplyToAllVisible={schedules.length > 1 ? async (hours : number, startOfWeek : string) => {
                                        handleApplyToAllVisible(schedule.employee.id, hours, startOfWeek)
                                    } : undefined }
                                />
                            ))}
                        </TableRow>
                    ))}
                    <TotalsRow projectSchedules={schedules} label="Total Hours"/>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ProjectScheduleTable;
